import React, {
  MutableRefObject,
  useEffect,
  useRef,
  useState,
  forwardRef,
} from "react";
import {
  ButtonTypesMultiRange,
  SliderMultipleRangeProps,
  RefsMultiRange,
} from "./slider-multiple-range.types";
import classNames from "classnames";
import {
  changeValuesAndUpdate,
  computeOffset,
  computeTrackWidth,
  computeValueBasedOnDragPosition,
  getNumberOfDecimalPlaces,
  updateValuesOnBlur,
} from "./slider.util";
import { getOffset, getSliderWidth } from "./slider.util";
import {
  ChangeValuesAndUpdate,
  MouseOrTouchEvent,
  ReactMouseOrTouchEvent,
} from "./slider.types";
import { TextInputBase } from "../text-input/text-input-base.jsx";

export interface SliderMultipleRangeWithForwardRef
  extends React.ForwardRefExoticComponent<
    SliderMultipleRangeProps & React.RefAttributes<() => RefsMultiRange>
  > {}

export const SliderMultipleRange = forwardRef<
  () => RefsMultiRange,
  SliderMultipleRangeProps
>(
  (
    {
      label,
      onChange,
      minValue: minValueProp,
      maxValue: maxValueProp,
      value: valueProp,
      minRange,
      maxRange,
      startTitle = "Start",
      endTitle = "End",
      middleTitle = "Middle",
      disabled = false,
      step = "1",
      valueInputName = "value",
      minValueInputName = "minValue",
      maxValueInputName = "maxValue",
      defaultMinValue,
      defaultValue,
      defaultMaxValue,
      className,
      alternate = false,
      ...rest
    },
    ref
  ): React.ReactElement | null => {
    const defaultValueComputed = defaultValue !== undefined ? defaultValue : 0;
    const defaultMinValueComputed =
      defaultMinValue !== undefined ? defaultMinValue : 0;
    const defaultMaxValueComputed =
      defaultMaxValue !== undefined ? defaultMaxValue : 0;
    const valueComputed =
      valueProp !== undefined ? valueProp : defaultValueComputed;
    const minValueComputed =
      minValueProp !== undefined ? minValueProp : defaultMinValueComputed;
    const maxValueComputed =
      maxValueProp !== undefined ? maxValueProp : defaultMaxValueComputed;
    const { minValueRef, midValueRef, maxValueRef } = (ref as any)?.() || {};
    const [minValue, setMinValue] = useState<number>(minValueComputed);
    const [maxValue, setMaxValue] = useState<number>(maxValueComputed);
    const [value, setValue] = useState<number>(valueComputed);
    const [startActive, setStartActive] = useState(false);
    const [middleActive, setMiddleActive] = useState(false);
    const [endActive, setEndActive] = useState(false);

    const [numberOfDecimalPlaces, setNumberOfDecimalPlaces] = useState(
      getNumberOfDecimalPlaces(step)
    );
    const [stepValue, setStepValue] = useState(Number(step));

    useEffect(() => {
      setNumberOfDecimalPlaces(getNumberOfDecimalPlaces(step));
      setStepValue(Number(step));
    }, [step]);

    const [inputValue, setInputValue] = useState(
      valueComputed.toFixed(numberOfDecimalPlaces)
    );

    useEffect(() => {
      setInputValue(
        (Math.round(valueComputed / stepValue) * stepValue).toFixed(
          numberOfDecimalPlaces
        )
      );
    }, [numberOfDecimalPlaces, stepValue, valueComputed]);

    const [inputMinValue, setInputMinValue] = useState(
      minValueComputed.toFixed(numberOfDecimalPlaces)
    );

    useEffect(() => {
      setInputMinValue(
        (Math.round(minValueComputed / stepValue) * stepValue).toFixed(
          numberOfDecimalPlaces
        )
      );
    }, [numberOfDecimalPlaces, minValueComputed, stepValue]);

    const [inputMaxValue, setInputMaxValue] = useState(
      maxValueComputed.toFixed(numberOfDecimalPlaces)
    );

    useEffect(() => {
      setInputMaxValue(
        (Math.round(maxValueComputed / stepValue) * stepValue).toFixed(
          numberOfDecimalPlaces
        )
      );
    }, [numberOfDecimalPlaces, maxValueComputed, stepValue]);

    const [startBtnOffset, setStartBtnOffset] = useState("0%");

    const [middleBtnOffset, setMiddleBtnOffset] = useState("0%");

    const [endBtnOffset, setEndBtnOffset] = useState("0%");

    // Ref for track component to get details from dom like width
    const trackEl = useRef<HTMLSpanElement>(null);
    const sliderEl = useRef() as MutableRefObject<HTMLDivElement>;
    const startButtonEl = useRef() as MutableRefObject<HTMLButtonElement>;
    const middleButtonEl = useRef() as MutableRefObject<HTMLButtonElement>;
    const endButtonEl = useRef() as MutableRefObject<HTMLButtonElement>;

    useEffect(() => {
      const roundedToStepValue =
        Math.round(valueComputed / stepValue) * stepValue;
      setValue(roundedToStepValue);
      setInputValue(roundedToStepValue.toFixed(numberOfDecimalPlaces));
    }, [numberOfDecimalPlaces, stepValue, valueComputed]);

    useEffect(() => {
      const roundedToStepMinValue =
        Math.round(minValueComputed / stepValue) * stepValue;
      setMinValue(roundedToStepMinValue);
      setInputMinValue(roundedToStepMinValue.toFixed(numberOfDecimalPlaces));
    }, [minValueComputed, numberOfDecimalPlaces, stepValue]);

    useEffect(() => {
      const roundedToStepMaxValue =
        Math.round(maxValueComputed / stepValue) * stepValue;
      setMaxValue(roundedToStepMaxValue);
      setInputMaxValue(roundedToStepMaxValue.toFixed(numberOfDecimalPlaces));
    }, [maxValueComputed, numberOfDecimalPlaces, stepValue]);

    useEffect(() => {
      const sliderWidth = getSliderWidth(sliderEl.current);
      setStartBtnOffset(
        computeOffset({
          minValue: minRange,
          maxValue: maxRange,
          value: minValue,
          isButton: true,
          ceilValue: value,
          floorValue: minRange,
          sliderWidth,
          stepValue,
          buttonWidth: startButtonEl.current.offsetWidth,
        }).buttonOffset
      );

      setMiddleBtnOffset(
        computeOffset({
          minValue: minRange,
          maxValue: maxRange,
          value,
          isButton: true,
          ceilValue: maxValue,
          floorValue: minValue,
          sliderWidth,
          stepValue,
          buttonWidth: middleButtonEl.current.offsetWidth,
        }).buttonOffset
      );

      setEndBtnOffset(
        computeOffset({
          minValue: minRange,
          maxValue: maxRange,
          value: maxValue,
          isButton: true,
          ceilValue: maxRange,
          floorValue: value,
          sliderWidth,
          stepValue,
          buttonWidth: endButtonEl.current.offsetWidth,
        }).buttonOffset
      );
    }, [minRange, maxRange, minValue, value, maxValue, stepValue]);

    // Resize slider on window resize
    const [, setResizeTS] = useState(0);
    const onResize = (e: Event): void => setResizeTS(e.timeStamp);
    useEffect(() => {
      window.addEventListener("resize", onResize);
      return () => {
        window.removeEventListener("resize", onResize);
      };
    });

    if (minRange >= maxRange || minValue > maxValue) {
      console.warn("Pass valid props otherwise slider won't render");
      return null;
    }

    const startBtnClass = classNames("spark-slider__handle", {
      active: startActive,
    });

    const middleBtnClass = classNames("spark-slider__handle", {
      active: middleActive,
    });

    const endBtnClass = classNames("spark-slider__handle", {
      active: endActive,
    });

    const getActiveIndex = (): number | undefined => {
      if (startActive) {
        return 0;
      }
      if (middleActive) {
        return 1;
      }
      if (endActive) {
        return 2;
      }
      return undefined;
    };

    const updateInputMinValue = (v: number): void => {
      let newValue = v;
      if (v >= maxValue || v >= value) {
        newValue = value - stepValue;
      }
      const roundedToStepMinValue =
        Math.round(newValue / stepValue) * stepValue;
      setInputMinValue(roundedToStepMinValue.toFixed(numberOfDecimalPlaces));
      setMinValue(roundedToStepMinValue);
      onChange?.({
        min: newValue,
        max: maxValue,
        value: value,
        activeThumb: 0,
      });
    };

    const updateValue = (v: number): void => {
      let newValue = v;
      if (v <= minValue) {
        newValue = minValue + stepValue;
      } else if (v >= maxValue) {
        newValue = maxValue - stepValue;
      }
      const roundedToStepValue = Math.round(newValue / stepValue) * stepValue;
      setInputValue(roundedToStepValue.toFixed(numberOfDecimalPlaces));
      setValue(roundedToStepValue);
      onChange?.({
        min: minValue,
        max: maxValue,
        value: newValue,
        activeThumb: 1,
      });
    };

    const updateInputMaxValue = (v: number): void => {
      let newValue = v;
      if (v <= value) {
        newValue = value + stepValue;
      } else if (v > maxRange) {
        newValue = maxRange;
      }
      const roundedToStepMaxValue =
        Math.round(newValue / stepValue) * stepValue;
      setInputMaxValue(roundedToStepMaxValue.toFixed(numberOfDecimalPlaces));
      setMaxValue(roundedToStepMaxValue);
      onChange?.({
        min: minValue,
        max: newValue,
        value: value,
        activeThumb: 2,
      });
    };

    const handleKeyEventsOnInput = (
      event: React.KeyboardEvent,
      identifier: ButtonTypesMultiRange
    ): void => {
      changeSliderValueOnKeyDown(event, identifier, false);
    };

    /**
     * Handle keystrokes on the button on focus
     * 1. up or right will increment until the maxValue for all buttons
     * 2. down or left will decrement until the minValue for all buttons
     */
    const changeSliderValueOnKeyDown = (
      { key }: React.KeyboardEvent,
      identifier: ButtonTypesMultiRange,
      isButton: boolean = true
    ): void => {
      if (disabled) {
        return;
      }

      let upDaterFn;
      let currentMinValue;
      let currentMaxValue;
      let currentValue;
      if (identifier === ButtonTypesMultiRange.START_BUTTON) {
        upDaterFn = updateInputMinValue;
        currentMinValue = minRange;
        currentMaxValue = value - stepValue;
        currentValue = isButton ? minValue : Number(inputMinValue);
      } else if (identifier === ButtonTypesMultiRange.MIDDLE_BUTTON) {
        upDaterFn = updateValue;
        currentMinValue = minValue + stepValue;
        currentMaxValue = maxValue - stepValue;
        currentValue = isButton ? value : Number(inputValue);
      } else if (identifier === ButtonTypesMultiRange.END_BUTTON) {
        upDaterFn = updateInputMaxValue;
        currentMinValue = value + stepValue;
        currentMaxValue = maxRange;
        currentValue = isButton ? maxValue : Number(inputMaxValue);
      } else {
        return;
      }

      changeValuesAndUpdate({
        key,
        upDaterFn,
        currentMinValue,
        currentValue,
        step: stepValue,
        decimalsInStep: numberOfDecimalPlaces,
        currentMaxValue,
        isButton,
      } as ChangeValuesAndUpdate);
    };

    const onMouseMoveStart = (e: MouseEvent | TouchEvent): void => {
      onMouseMove(e, ButtonTypesMultiRange.START_BUTTON);
    };
    const onMouseMoveMiddle = (e: MouseEvent | TouchEvent): void => {
      onMouseMove(e, ButtonTypesMultiRange.MIDDLE_BUTTON);
    };
    const onMouseMoveEnd = (e: MouseEvent | TouchEvent): void => {
      onMouseMove(e, ButtonTypesMultiRange.END_BUTTON);
    };

    // Mouse is moved on the slider. The track fill and button position to be computed
    const onMouseMove = (
      e: MouseOrTouchEvent,
      btnClicked: ButtonTypesMultiRange
    ): void => {
      const sliderObject = getOffset(sliderEl.current);
      let updaterMethod;
      if (typeof sliderObject === "boolean") {
        return;
      }
      switch (btnClicked) {
        case ButtonTypesMultiRange.START_BUTTON:
          updaterMethod = updateInputMinValue;
          break;
        case ButtonTypesMultiRange.MIDDLE_BUTTON:
          updaterMethod = updateValue;
          break;
        default:
          updaterMethod = updateInputMaxValue;
      }
      const dragPosition =
        "clientX" in e
          ? e.clientX - sliderObject.left
          : e.touches[0].clientX - sliderObject.left;

      // The event occurred in between min and max value range.
      const newValue = computeValueBasedOnDragPosition({
        dragPosition,
        sliderWidth: sliderObject.width,
        minValue: minRange,
        maxValue: maxRange,
        step: stepValue,
        decimalsInStep: numberOfDecimalPlaces,
      });
      updaterMethod(newValue);
    };

    const onMouseUp = (): void => {
      if (disabled) {
        return;
      }

      setStartActive(false);
      setMiddleActive(false);
      setEndActive(false);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("mousemove", onMouseMoveStart);
      window.removeEventListener("mousemove", onMouseMoveMiddle);
      window.removeEventListener("mousemove", onMouseMoveEnd);

      window.removeEventListener("touchend", onMouseUp);
      window.removeEventListener("touchmove", onMouseMoveStart);
      window.removeEventListener("touchmove", onMouseMoveMiddle);
      window.removeEventListener("touchmove", onMouseMoveEnd);
    };

    // Triggered on mouse down event on the button. User is about to drag
    const changeSliderValue1 = (e: ReactMouseOrTouchEvent): void => {
      if (disabled) {
        return;
      }
      e.cancelable && e.preventDefault();
      startButtonEl.current.focus();

      setStartActive(true);

      window.addEventListener("mousemove", onMouseMoveStart);
      window.addEventListener("mouseup", onMouseUp);

      window.addEventListener("touchmove", onMouseMoveStart);
      window.addEventListener("touchend", onMouseUp);
    };
    const changeSliderValue2 = (e: ReactMouseOrTouchEvent): void => {
      if (disabled) {
        return;
      }
      e.cancelable && e.preventDefault();
      middleButtonEl.current.focus();

      setMiddleActive(true);

      window.addEventListener("mousemove", onMouseMoveMiddle);
      window.addEventListener("mouseup", onMouseUp);

      window.addEventListener("touchmove", onMouseMoveMiddle);
      window.addEventListener("touchend", onMouseUp);
    };
    const changeSliderValue3 = (e: ReactMouseOrTouchEvent): void => {
      if (disabled) {
        return;
      }
      e.cancelable && e.preventDefault();
      endButtonEl.current.focus();
      setEndActive(true);

      window.addEventListener("mousemove", onMouseMoveEnd);
      window.addEventListener("mouseup", onMouseUp);

      window.addEventListener("touchmove", onMouseMoveEnd);
      window.addEventListener("touchend", onMouseUp);
    };

    const sliderClassNames = classNames(
      {
        "spark-slider--secondary": alternate,
      },
      "spark-range-slider",
      className
    );
    return (
      <div
        className={sliderClassNames}
        data-active-index={getActiveIndex()}
        {...rest}
      >
        <label className="spark-label" htmlFor={minValueInputName || ""}>
          {label}
        </label>
        <TextInputBase
          ref={minValueRef}
          type="number"
          className={classNames({ active: startActive })}
          name={minValueInputName || ""}
          id={minValueInputName || ""}
          min={minRange}
          max={value - stepValue}
          value={inputMinValue}
          step={step}
          title={startTitle}
          aria-label={startTitle}
          disabled={disabled}
          onKeyDown={(e): void =>
            handleKeyEventsOnInput(e, ButtonTypesMultiRange.START_BUTTON)
          }
          onChange={(e): void => setInputMinValue(e.target.value)}
          onBlur={(): void =>
            updateValuesOnBlur({
              updaterFn: updateInputMinValue,
              minValue: minRange,
              maxValue: value,
              valueFromInput: inputMinValue,
            })
          }
          isSlider={true}
        />
        <span className="spark-range-slider__input-divider" />
        <TextInputBase
          ref={midValueRef}
          type="number"
          name={valueInputName || ""}
          id={valueInputName || ""}
          min={minValue + stepValue}
          max={maxValue - stepValue}
          value={inputValue}
          step={step}
          title={middleTitle}
          aria-label={middleTitle}
          disabled={disabled}
          onKeyDown={(e): void =>
            handleKeyEventsOnInput(e, ButtonTypesMultiRange.MIDDLE_BUTTON)
          }
          onChange={(e): void => setInputValue(e.target.value)}
          onBlur={(): void =>
            updateValuesOnBlur({
              updaterFn: updateValue,
              minValue,
              maxValue,
              valueFromInput: inputValue,
            })
          }
          isSlider={true}
        />
        <span className="spark-range-slider__input-divider" />
        <TextInputBase
          ref={maxValueRef}
          type="number"
          name={maxValueInputName || ""}
          id={maxValueInputName || ""}
          min={value + stepValue}
          max={maxRange}
          value={inputMaxValue}
          step={step}
          title={endTitle}
          aria-label={endTitle}
          disabled={disabled}
          onKeyDown={(e): void =>
            handleKeyEventsOnInput(e, ButtonTypesMultiRange.END_BUTTON)
          }
          onChange={(e): void => setInputMaxValue(e.target.value)}
          onBlur={(): void =>
            updateValuesOnBlur({
              updaterFn: updateInputMaxValue,
              minValue: value,
              maxValue: maxRange,
              valueFromInput: inputMaxValue,
            })
          }
          isSlider={true}
        />
        <div className="spark-slider__controls" ref={sliderEl}>
          <button
            type="button"
            tabIndex={disabled ? -1 : undefined}
            className={startBtnClass}
            title={startTitle}
            ref={startButtonEl}
            role="slider"
            aria-orientation="horizontal"
            aria-valuemin={minRange}
            aria-valuemax={value - stepValue}
            aria-valuenow={minValue}
            aria-valuetext={"" + minValue}
            aria-label={startTitle}
            aria-controls={minValueInputName || ""}
            data-value={minValue}
            onTouchStart={changeSliderValue1}
            onMouseDown={changeSliderValue1}
            style={{ left: startBtnOffset }}
            onFocus={(): void => setStartActive(true)}
            onBlur={(): void => setStartActive(false)}
            onKeyDown={(e): void =>
              changeSliderValueOnKeyDown(e, ButtonTypesMultiRange.START_BUTTON)
            }
          />
          <button
            type="button"
            tabIndex={disabled ? -1 : undefined}
            ref={middleButtonEl}
            className={middleBtnClass}
            title={middleTitle}
            role="slider"
            aria-controls={valueInputName || ""}
            aria-orientation="horizontal"
            aria-valuemin={minValue + stepValue}
            aria-valuemax={maxValue - stepValue}
            aria-valuenow={value}
            aria-valuetext={"" + value}
            aria-label={middleTitle}
            data-value={value}
            onTouchStart={changeSliderValue2}
            onMouseDown={changeSliderValue2}
            onFocus={(): void => setMiddleActive(true)}
            onBlur={(): void => setMiddleActive(false)}
            style={{ left: middleBtnOffset }}
            onKeyDown={(e): void =>
              changeSliderValueOnKeyDown(e, ButtonTypesMultiRange.MIDDLE_BUTTON)
            }
          />
          <button
            type="button"
            ref={endButtonEl}
            tabIndex={disabled ? -1 : undefined}
            className={endBtnClass}
            title={endTitle}
            role="slider"
            aria-controls={maxValueInputName || ""}
            aria-orientation="horizontal"
            aria-valuemin={minValue + stepValue}
            aria-valuemax={maxRange}
            aria-valuenow={maxValue}
            aria-valuetext={"" + maxValue}
            aria-label={endTitle}
            data-value={maxValue}
            onTouchStart={changeSliderValue3}
            onMouseDown={changeSliderValue3}
            onFocus={(): void => setEndActive(true)}
            onBlur={(): void => setEndActive(false)}
            style={{ left: endBtnOffset }}
            onKeyDown={(e): void =>
              changeSliderValueOnKeyDown(e, ButtonTypesMultiRange.END_BUTTON)
            }
          />
          <span className="spark-slider__track" ref={trackEl}>
            <span
              className="spark-slider__track-fill"
              style={{
                width: computeTrackWidth(startBtnOffset, endBtnOffset),
                left: startBtnOffset,
              }}
            />
          </span>
        </div>
      </div>
    );
  }
) as SliderMultipleRangeWithForwardRef;

SliderMultipleRange.displayName = "SliderMultipleRange";
